import { AGRAVOS, DESCRICAO_AGRAVOS, MENSAGEM_EM_DESENVOLVIMENTO } from '@metaepi/shared';
import { Activity, CircleHelp, House, Wrench } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { NavLink } from 'react-router';
import { SeloStatus } from '../components/ui';
import { useSessao } from '../features/auth/contexto';
import { cn } from '../lib/cn';
import {
  ITENS_ADMINISTRACAO,
  ITENS_FASES,
  ITENS_PRINCIPAIS,
  itemVisivel,
  PERFIS_AGRAVOS,
  type ItemNavegacao,
} from './navegacao';

const classeLink = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium',
    isActive ? 'bg-primaria-suave text-primaria' : 'text-texto hover:bg-superficie-2',
  );

function Grupo({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <p className="px-3 pt-4 pb-1 text-xs font-semibold tracking-wide text-texto-suave uppercase">
        {titulo}
      </p>
      <ul className="flex flex-col gap-0.5">{children}</ul>
    </div>
  );
}

function Links({ itens, aoNavegar }: { itens: ItemNavegacao[]; aoNavegar?: () => void }) {
  return itens.map((item) => (
    <li key={item.caminho}>
      <NavLink to={item.caminho} className={classeLink} onClick={aoNavegar}>
        {item.rotulo}
      </NavLink>
    </li>
  ));
}

export function MenuLateral({ aoNavegar }: { aoNavegar?: () => void }) {
  const { conta, pode } = useSessao();
  const [aviso, setAviso] = useState('');
  if (!conta) return null;

  const perfil = conta.usuario.perfil;
  const filtrar = (itens: ItemNavegacao[]) => itens.filter((i) => itemVisivel(i, perfil, pode));
  const fases = filtrar(ITENS_FASES);
  const principais = filtrar(ITENS_PRINCIPAIS);
  const administracao = filtrar(ITENS_ADMINISTRACAO);
  const veAgravos = PERFIS_AGRAVOS.includes(perfil);

  return (
    <nav aria-label="Menu principal" className="flex flex-col p-3">
      <ul>
        <li>
          <NavLink to="/" end className={classeLink} onClick={aoNavegar}>
            <House className="size-4" aria-hidden="true" />
            Início
          </NavLink>
        </li>
      </ul>

      {fases.length > 0 && (
        <Grupo titulo="Linha de cuidado">
          <Links itens={fases} aoNavegar={aoNavegar} />
        </Grupo>
      )}

      {veAgravos && (
        <>
          <Grupo titulo="Agravos acompanhados">
            {AGRAVOS.map((agravo) => {
              const rotulo = DESCRICAO_AGRAVOS[agravo].rotulo;
              return conta.agravosDisponiveis[agravo] ? (
                <li key={agravo}>
                  <NavLink to={`/agravos/${agravo}`} className={classeLink} onClick={aoNavegar}>
                    <Activity className="size-4" aria-hidden="true" />
                    {rotulo}
                  </NavLink>
                </li>
              ) : (
                <li key={agravo}>
                  {/* D-014: visível, mas sem navegar. Ao clicar, explica o motivo. */}
                  <button
                    type="button"
                    aria-disabled="true"
                    onClick={() => setAviso(`${rotulo}: ${MENSAGEM_EM_DESENVOLVIMENTO}`)}
                    className="flex min-h-11 w-full cursor-not-allowed flex-col items-start gap-1 rounded-lg px-3 py-2 text-left text-sm text-texto-suave hover:bg-superficie-2"
                  >
                    <span className="flex items-center gap-2">
                      <Wrench className="size-4" aria-hidden="true" />
                      {rotulo}
                    </span>
                    <SeloStatus tipo="desenvolvimento" className="ml-6">
                      Em desenvolvimento
                    </SeloStatus>
                  </button>
                </li>
              );
            })}
          </Grupo>
          <p
            role="status"
            aria-live="polite"
            className={cn('px-3 text-xs text-desenvolvimento', aviso && 'pt-2')}
          >
            {aviso}
          </p>
        </>
      )}

      {principais.length > 0 && (
        <Grupo titulo="Atendimento e gestão">
          <Links itens={principais} aoNavegar={aoNavegar} />
        </Grupo>
      )}

      {administracao.length > 0 && (
        <Grupo titulo="Administração">
          <Links itens={administracao} aoNavegar={aoNavegar} />
        </Grupo>
      )}

      <ul className="mt-4 border-t border-borda pt-4">
        <li>
          <NavLink to="/ajuda" className={classeLink} onClick={aoNavegar}>
            <CircleHelp className="size-4" aria-hidden="true" />
            Ajuda
          </NavLink>
        </li>
      </ul>
    </nav>
  );
}
